import { RewardIcon } from '@ui/shared/icons/RewardIcon';

/**
 * Curated icon set (settings-rewards.md §4) — searchable popover grid,
 * shared by the badge editor and the level-name editor (§1.3's "same picker
 * as the badge editor"). Only icons this build actually maps in
 * `RewardIcon`'s `ICON_MAP` are offered — offering an icon id with no
 * mapped component would silently fall back to the neutral star glyph,
 * which would be a confusing picker experience (pick X, see a star). The
 * curated-31 list from the spec is the target; this build's subset is
 * exactly what `RewardIcon` maps today (see that file's own "map what's
 * used" policy note).
 */
const PICKER_ICONS: readonly string[] = [
  'flame',
  'zap',
  'star',
  'compass',
  'target',
  'mountain',
  'mountain-snow',
  'footprints',
  'timer',
  'shield-check',
  'sunrise',
  'calendar-check',
  'calendar-range',
  'sliders-horizontal',
  'list-checks',
  'anchor',
  'route',
  'wind',
  'telescope',
  'waves',
  'map',
];

export function IconPickerPopover({
  selected,
  onSelect,
  onClose,
}: {
  readonly selected: string;
  readonly onSelect: (iconId: string) => void;
  readonly onClose: () => void;
}) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
      <div
        role="dialog"
        aria-label="Choose an icon"
        style={{
          position: 'absolute',
          zIndex: 41,
          top: 'calc(100% + var(--space-2))',
          left: 0,
          background: 'var(--surface-overlay)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-2)',
          padding: 'var(--space-3)',
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 'var(--space-2)',
          width: '280px',
        }}
      >
        {PICKER_ICONS.map((iconId) => (
          <button
            key={iconId}
            type="button"
            onClick={() => onSelect(iconId)}
            aria-label={iconId}
            aria-pressed={iconId === selected}
            style={{
              width: 'var(--touch-target-min)',
              height: 'var(--touch-target-min)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-md)',
              background: iconId === selected ? 'var(--surface-hover)' : 'transparent',
              border: iconId === selected ? '1px solid var(--hue-primary)' : '1px solid transparent',
            }}
          >
            <RewardIcon iconId={iconId} size={18} color={iconId === selected ? 'var(--hue-primary-vivid)' : 'var(--text-secondary)'} />
          </button>
        ))}
      </div>
    </>
  );
}
